import type { IconProps } from '@carboncopyco/icons';
import { DownOutlineIcon, VOutlineIcon } from '@carboncopyco/icons';
import { capitalize, useClickAwayWithShadow } from '@carboncopyco/utils';
import { type ReactElement, useRef, useState } from 'react';
import { ColorHex } from '../../constants';
import { DropdownMenu, DropdownMenuItem } from '../DropdownMenu';
import { TooltipBubble } from '../TooltipBubble';
import { ButtonDropdown } from './ButtonDropdown';

export interface ButtonDropdownItem {
  label: string;
  id: string | number;
  icon?: ReactElement<IconProps>;
  description?: string;
  disabled?: boolean;
  tooltip?: string;
}

export interface ButtonDropdownMenuProps {
  value: ButtonDropdownItem;
  onSelect: (item: ButtonDropdownItem) => void;
  items: ButtonDropdownItem[];
  noCheck?: boolean;
  disabled?: boolean;
  leftIcon?: ReactElement<IconProps>;
  primary?: boolean;
  accent1?: boolean;
  secondary?: boolean;
  natural?: boolean;
  customLabel?: string;
}

export const ButtonDropdownMenu = ({
  value,
  onSelect,
  items,
  primary,
  accent1,
  secondary,
  noCheck,
  disabled,
  natural,
  customLabel,
  leftIcon,
}: ButtonDropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickAwayWithShadow(() => setIsOpen(false), dropdownRef);

  return (
    <div ref={dropdownRef}>
      <DropdownMenu
        visible={isOpen}
        button={
          <ButtonDropdown
            isSelected={isOpen}
            disabled={disabled}
            primary={primary}
            secondary={secondary}
            accent1={accent1}
            natural={natural}
            label={value.label}
            customLabel={customLabel}
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            leftIcon={customLabel ? leftIcon : value.icon}
            rightIcon={<DownOutlineIcon />}
          />
        }
      >
        {items.map((item) => (
          <TooltipBubble
            key={item.id}
            placement="top"
            title={item.tooltip || ''}
            disabled={!item.tooltip}
            offset={[0, -5]}
          >
            <DropdownMenuItem
              key={item.id}
              iconRight={
                !noCheck && value.id === item.id ? (
                  <VOutlineIcon color={ColorHex.natural} />
                ) : undefined
              }
              onClick={() => {
                setIsOpen(false);
                onSelect?.(item);
              }}
              iconLeft={item.icon}
              label={capitalize(item.label)}
              disabled={item.disabled}
            />
          </TooltipBubble>
        ))}
      </DropdownMenu>
    </div>
  );
};
